/**
 * 资源路径修复工具
 * 用于统一处理网站资源路径，确保在生产环境中正确使用CDN前缀
 */

/**
 * 检查路径是否为视频文件
 * @param {string} path - 文件路径
 * @returns {boolean} 是否为视频文件
 */
function isVideoFile(path) {
  if (!path || typeof path !== 'string') return false;
  const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];
  const lowerPath = path.toLowerCase();
  return videoExtensions.some(ext => lowerPath.includes(ext));
}

/**
 * 获取 CDN 配置（用于视频等媒体资源）
 * 优先从 window.__INITIAL_SITE_CONFIG__ 获取（客户端），
 * 其次从 globalThis.__INITIAL_SITE_CONFIG__ 获取（SSR 服务端），
 * 最后使用构建时注入的全局变量
 * @returns {{ useCdn: boolean, cdnPrefix: string, videoCdnPrefix: string }}
 */
function getCdnConfig() {
  // 客户端：优先从 window 获取
  if (typeof window !== 'undefined' && window.__INITIAL_SITE_CONFIG__) {
    return {
      useCdn: window.__INITIAL_SITE_CONFIG__.useCdn || false,
      cdnPrefix: window.__INITIAL_SITE_CONFIG__.cdnPrefix || '',
      videoCdnPrefix: window.__INITIAL_SITE_CONFIG__.videoCdnPrefix || ''
    };
  }

  // SSR 服务端：从 globalThis 获取（server.js 在渲染前设置）
  if (typeof globalThis !== 'undefined' && globalThis.__INITIAL_SITE_CONFIG__) {
    return {
      useCdn: globalThis.__INITIAL_SITE_CONFIG__.useCdn || false,
      cdnPrefix: globalThis.__INITIAL_SITE_CONFIG__.cdnPrefix || '',
      videoCdnPrefix: globalThis.__INITIAL_SITE_CONFIG__.videoCdnPrefix || ''
    };
  }

  // 使用构建时注入的全局变量
  if (typeof __CDN_USE__ !== 'undefined' && typeof __CDN_PREFIX__ !== 'undefined') {
    return {
      useCdn: __CDN_USE__,
      cdnPrefix: __CDN_PREFIX__,
      videoCdnPrefix: typeof __VIDEO_CDN_PREFIX__ !== 'undefined' ? __VIDEO_CDN_PREFIX__ : ''
    };
  }

  return { useCdn: false, cdnPrefix: '', videoCdnPrefix: '' };
}

/**
 * 为路径添加域名前缀
 * @param {string} path - 图片路径
 * @param {string} prefix - 域名前缀（如 cdnPrefix）
 * @returns {string} 带前缀的完整路径
 */
function addDomainPrefix(path, prefix) {
  if (!path || !prefix) return path;

  // 如果路径以 /website/ 开头，直接添加前缀
  if (path.startsWith('/website/')) {
    return `${prefix}${path}`;
  }
  // 如果路径以 /images/ 开头，转换为 /website/images/ 再添加前缀
  if (path.startsWith('/images/')) {
    return `${prefix}/website${path}`;
  }
  // 其他情况，如果路径以 / 开头，直接添加前缀
  if (path.startsWith('/')) {
    return `${prefix}${path}`;
  }

  return path;
}

/**
 * 获取媒体资源的完整 URL
 * - 视频文件（.mp4 等）使用 videoCdnPrefix（视频专用CDN）
 * - 其他资源根据 useCdn 配置决定使用 cdnPrefix 或 ApiDomainName
 * 参考参考项目的实现方式
 * @param {string} path - 媒体资源路径，如 '/img/v2/teacher/ru/xxx.mp4'
 * @returns {string} 带前缀的完整路径或原路径
 */
export function getMediaUrl(path) {
  // 如果路径为空或不是字符串，直接返回
  if (!path || typeof path !== 'string') {
    return path;
  }

  // 如果已经是完整的 URL，直接返回
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  // 检查是否为视频文件
  const isVideo = isVideoFile(path);

  // 视频文件使用视频专用CDN前缀（优先使用 videoCdnPrefix）
  if (isVideo) {
    const videoCdnPrefix = getCdnPrefixForVideo();
    if (videoCdnPrefix) {
      return addDomainPrefix(path, videoCdnPrefix);
    }
    return path;
  }

  // 非视频文件（图片等）使用 fixAssetUrl 处理
  return fixAssetUrl(path);
}

/**
 * 获取视频 CDN 前缀（用于视频资源，无论 useCdn 值如何）
 * 优先使用 videoCdnPrefix，如果未配置则回退到 cdnPrefix
 * @returns {string|null} 视频 CDN 前缀
 */
function getCdnPrefixForVideo() {
  const { videoCdnPrefix, cdnPrefix } = getCdnConfig();
  // 优先使用视频专用CDN前缀，如果未配置则回退到通用CDN前缀
  return videoCdnPrefix || cdnPrefix || null;
}

/**
 * 检查当前环境是否是开发环境
 * 兼容客户端和服务端
 */
function isDevelopment() {
  // 浏览器环境
  if (typeof window !== 'undefined' && typeof __ENV__ !== 'undefined') {
    return __ENV__ === 'development';
  }
  // Node环境
  else if (typeof process !== 'undefined' && process.env) {
    return process.env.NODE_ENV === 'development';
  }
  return false;
}

/**
 * 检查当前环境是否是生产环境
 * 兼容客户端和服务端
 * 支持 production 和 release 环境
 * @returns {boolean} 是否为生产环境
 */
function isProduction() {
  // 浏览器环境
  if (typeof window !== 'undefined' && typeof __ENV__ !== 'undefined') {
    // 修复：支持 production 和 release 环境
    return __ENV__ === 'production' || __ENV__ === 'release';
  }
  // Node环境
  else if (typeof process !== 'undefined' && process.env) {
    return process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'release';
  }
  return false;
}

/**
 * 检测是否应该使用CDN路径
 * 兼容客户端（window）和 SSR 服务端（globalThis）
 * @returns {boolean} 是否应该使用CDN路径
 */
export function shouldUseCdn() {
  // 检查是否是生产环境
  if (!isProduction()) return false;

  // 客户端：从 window 获取配置
  if (typeof window !== 'undefined' && window.__INITIAL_SITE_CONFIG__) {
    const { useCdn, cdnPrefix } = window.__INITIAL_SITE_CONFIG__;
    return Boolean(useCdn && cdnPrefix);
  }

  // SSR 服务端：从 globalThis 获取配置（server.js 在渲染前设置）
  if (typeof globalThis !== 'undefined' && globalThis.__INITIAL_SITE_CONFIG__) {
    const { useCdn, cdnPrefix } = globalThis.__INITIAL_SITE_CONFIG__;
    return Boolean(useCdn && cdnPrefix);
  }

  return false;
}

/**
 * 获取CDN前缀
 * 兼容客户端（window）和 SSR 服务端（globalThis）
 * @returns {string|null} CDN前缀，如果不应使用CDN则返回null
 */
export function getCdnPrefix() {
  if (!shouldUseCdn()) return null;

  // 客户端：从 window 获取
  if (typeof window !== 'undefined' && window.__INITIAL_SITE_CONFIG__) {
    return window.__INITIAL_SITE_CONFIG__.cdnPrefix || null;
  }

  // SSR 服务端：从 globalThis 获取
  if (typeof globalThis !== 'undefined' && globalThis.__INITIAL_SITE_CONFIG__) {
    return globalThis.__INITIAL_SITE_CONFIG__.cdnPrefix || null;
  }

  return null;
}

/**
 * 修复资源URL，确保在生产环境中使用CDN前缀
 * 
 * @param {string} url - 要修复的URL
 * @returns {string} 修复后的URL
 */
export function fixAssetUrl(url) {
  // 如果URL是空或不是字符串，直接返回
  if (!url || typeof url !== 'string') return url;

  // 如果是完整的URL（包括协议），直接返回
  if (url.match(/^(https?:)?\/\//)) return url;

  try {
    // 开发环境需要特殊处理路径
    if (isDevelopment()) {
      const { cdnPrefix } = getCdnConfig();
      // /img/ 下的图片等资源放在静态 CDN，开发环境也使用当前站点配置的测试 CDN，便于本地预览
      if (url.startsWith('/img/') && !isVideoFile(url) && cdnPrefix) {
        return addDomainPrefix(url, cdnPrefix);
      }
      // 开发环境中处理字体文件路径
      if (url.startsWith('/website/fonts/')) {
        return url.replace('/website/fonts/', '/fonts/');
      }
      // 开发环境中处理图片路径
      if (url.startsWith('/website/images/')) {
        return url.replace('/website/images/', '/images/');
      }
      return url;
    }

    // 视频文件始终使用 videoCdnPrefix（视频专用CDN，无论 useCdn 的值）
    if (isVideoFile(url)) {
      const videoCdnPrefix = getCdnPrefixForVideo();
      if (videoCdnPrefix) {
        if (url.startsWith('/website/')) {
          return `${videoCdnPrefix}${url}`;
        } else if (url.startsWith('/images/')) {
          return `${videoCdnPrefix}/website${url}`;
        } else if (url.startsWith('/')) {
          return `${videoCdnPrefix}${url}`;
        } else {
          return `${videoCdnPrefix}/${url}`;
        }
      }
      return url;
    }

    // 非视频资源：如果不应该使用CDN，直接返回
    if (!shouldUseCdn()) return url;

    const cdnPrefix = getCdnPrefix();
    if (!cdnPrefix) return url;

    // 处理以/website/开头的路径
    if (url.startsWith('/website/')) {
      return `${cdnPrefix}${url}`;
    }

    // 处理以/images/开头的路径（需要转换为/website/images/）
    if (url.startsWith('/images/')) {
      return `${cdnPrefix}/website${url}`;
    }

    // 处理以/fonts/开头的路径（需要转换为/website/fonts/）
    if (url.startsWith('/fonts/')) {
      return `${cdnPrefix}/website${url}`;
    }

    // 其他以/开头的路径，可能也需要考虑添加CDN前缀
    if (url.startsWith('/')) {
      return `${cdnPrefix}${url}`;
    }
  } catch (e) {
    console.error('资源路径处理错误:', e);
  }

  // 其他情况直接返回
  return url;
}

/**
 * Vue插件，用于自动修复Vue组件内的资源路径
 */
export const AssetPathFixerPlugin = {
  install(app) {
    // 全局注入fixAssetUrl方法
    app.config.globalProperties.$fixAssetUrl = fixAssetUrl;

    // 添加全局指令v-asset-url
    app.directive('asset-url', {
      beforeMount(el, binding) {
        const attr = binding.arg || 'src';
        if (el.hasAttribute(attr)) {
          const originalUrl = el.getAttribute(attr);
          el.setAttribute(attr, fixAssetUrl(originalUrl));
        }
      },
      updated(el, binding) {
        const attr = binding.arg || 'src';
        if (el.hasAttribute(attr)) {
          const originalUrl = el.getAttribute(attr);
          el.setAttribute(attr, fixAssetUrl(originalUrl));
        }
      }
    });

    // 注意：不再使用 mixin 和 load 事件监听器来扫描 DOM
    // 原因：server.js 已完成 CDN 路径替换，客户端重复扫描会导致 hydration 后 DOM 变化，引起闪屏
    // 如果需要处理动态内容，请在组件内部使用 $fixAssetUrl 方法或 v-asset-url 指令
  }
};

/**
 * 修复DOM元素中的所有资源路径
 * @param {HTMLElement} rootElement - 根元素
 */
export function fixAllResourcePaths(rootElement) {

  if (!rootElement) return;

  try {
    // 处理所有图片的src属性
    const allImages = rootElement.querySelectorAll('img');
    allImages.forEach(img => {
      if (img.src && typeof img.src === 'string') {
        const originalSrc = img.getAttribute('src');
        if (originalSrc && !originalSrc.startsWith('http') && !originalSrc.startsWith('data:')) {
          const fixedSrc = fixAssetUrl(originalSrc);
          if (fixedSrc !== originalSrc) {
            img.src = fixedSrc;
          }
        }
      }
    });

    // 处理所有css背景图片
    const elementsWithStyle = rootElement.querySelectorAll('[style*="background"], [style*="background-image"]');
    elementsWithStyle.forEach(el => {
      const style = el.getAttribute('style');
      if (style && (style.includes('background') || style.includes('background-image'))) {
        const fixedStyle = fixStyleUrls(style);
        if (fixedStyle !== style) {
          el.setAttribute('style', fixedStyle);
        }
      }
    });

    // 处理链接中的href
    const links = rootElement.querySelectorAll('link[rel="stylesheet"], link[rel="preload"], link[rel="icon"], link[rel="shortcut icon"]');
    links.forEach(link => {
      const href = link.getAttribute('href');
      if (href && !href.startsWith('http') && !href.startsWith('data:')) {
        const fixedHref = fixAssetUrl(href);
        if (fixedHref !== href) {
          link.setAttribute('href', fixedHref);
        }
      }
    });

    // 处理video, audio等媒体元素
    const mediaElements = rootElement.querySelectorAll('video, audio');
    mediaElements.forEach(media => {
      const src = media.getAttribute('src');
      if (src && !src.startsWith('http') && !src.startsWith('data:')) {
        const fixedSrc = fixAssetUrl(src);
        if (fixedSrc !== src) {
          media.setAttribute('src', fixedSrc);
        }
      }

      // 处理source子元素
      const sources = media.querySelectorAll('source');
      sources.forEach(source => {
        const sourceSrc = source.getAttribute('src');
        if (sourceSrc && !sourceSrc.startsWith('http') && !sourceSrc.startsWith('data:')) {
          const fixedSourceSrc = fixAssetUrl(sourceSrc);
          if (fixedSourceSrc !== sourceSrc) {
            source.setAttribute('src', fixedSourceSrc);
          }
        }
      });
    });

    // 处理内联样式表
    const styleElements = rootElement.querySelectorAll('style');
    styleElements.forEach(styleEl => {
      const cssText = styleEl.textContent;
      if (cssText) {
        const fixedCssText = fixStyleUrls(cssText);
        if (fixedCssText !== cssText) {
          styleEl.textContent = fixedCssText;
        }
      }
    });

    // 处理特殊属性，如data-src (lazy loading)
    const elementsWithDataSrc = rootElement.querySelectorAll('[data-src]');
    elementsWithDataSrc.forEach(el => {
      const dataSrc = el.getAttribute('data-src');
      if (dataSrc && !dataSrc.startsWith('http') && !dataSrc.startsWith('data:')) {
        const fixedDataSrc = fixAssetUrl(dataSrc);
        if (fixedDataSrc !== dataSrc) {
          el.setAttribute('data-src', fixedDataSrc);
        }
      }
    });
  } catch (e) {
    // 静默处理错误
  }
}

/**
 * 处理CSS样式中的URL
 * @param {string} styleText - 样式文本
 * @returns {string} 修复后的样式文本
 */
function fixStyleUrls(styleText) {
  if (!styleText || typeof styleText !== 'string') return styleText;

  // 匹配样式中的url()表达式
  const urlRegex = /url\(['"]?([^'")]+)['"]?\)/g;
  return styleText.replace(urlRegex, (match, url) => {
    // 只处理本地资源路径
    if (url && !url.startsWith('http') && !url.startsWith('data:')) {
      const fixedUrl = fixAssetUrl(url);
      return `url('${fixedUrl}')`;
    }
    return match;
  });
}

/**
 * 为输出的HTML添加CDN前缀
 * 
 * @param {string} html - 原始HTML内容
 * @returns {string} 修复后的HTML内容
 */
export function fixHtmlAssetUrls(html) {
  if (!html) return html;

  try {
    // 生产环境CDN处理
    // 在服务器端执行时缺少window对象，需要额外检查
    // 这里直接从传入的参数中获取cdnPrefix
    const cdnPrefix = typeof window !== 'undefined' && window.__INITIAL_SITE_CONFIG__?.cdnPrefix;

    if (cdnPrefix) {

      // 替换CSS和JS资源链接
      html = html
        // 替换href属性中的/website/路径
        .replace(/(href=["'])\/website\//g, `$1${cdnPrefix}/website/`)
        // 替换src属性中的/website/路径
        .replace(/(src=["'])\/website\//g, `$1${cdnPrefix}/website/`)
        // 替换href属性中的/images/路径
        .replace(/(href=["'])\/images\//g, `$1${cdnPrefix}/website/images/`)
        // 替换src属性中的/images/路径
        .replace(/(src=["'])\/images\//g, `$1${cdnPrefix}/website/images/`)
        // 替换background-image:url('/images/...)路径
        .replace(/background-image:\s*url\(['"]?\/images\//g, `background-image:url(${cdnPrefix}/website/images/`)
        // 替换background:url('/images/...)路径
        .replace(/background:\s*url\(['"]?\/images\//g, `background:url(${cdnPrefix}/website/images/`)
        // 替换字体路径
        .replace(/(href=["'])\/fonts\//g, `$1${cdnPrefix}/website/fonts/`)
        .replace(/(src=["'])\/fonts\//g, `$1${cdnPrefix}/website/fonts/`);

      // 额外处理引号内的路径
      html = html
        .replace(/url\(['"]?\/website\/images\//g, `url(${cdnPrefix}/website/images/`)
        .replace(/url\(['"]?\/website\/fonts\//g, `url(${cdnPrefix}/website/fonts/`);
    }

    return html;
  } catch (e) {
    console.error('HTML资源路径处理错误:', e);
    return html; // 出错时返回原始HTML
  }
}

// 创建一个可以包装现有工具函数的辅助函数
export function wrapWithCdnSupport(originalFn) {
  return function (...args) {
    const result = originalFn(...args);
    return fixAssetUrl(result);
  };
}

/**
 * 判断属性名是否为图片或视频相关字段
 * @param {string} key - 属性名
 * @returns {boolean} 是否为图片或视频相关字段
 */
function isMediaField(key) {
  if (!key || typeof key !== 'string') return false;
  const lowerKey = key.toLowerCase();
  return lowerKey.includes('image') ||
    lowerKey.includes('icon') ||
    lowerKey.includes('qrcode') ||
    lowerKey.includes('font') ||
    lowerKey.includes('video') ||
    lowerKey.includes('source') ||
    lowerKey.includes('avatar') ||
    lowerKey.includes('bg');
}

/**
 * 处理数据对象中的图片和视频路径
 * 自动处理开发环境和生产环境的路径差异
 * @param {Object} data - 数据对象
 * @returns {Object} 处理后的数据对象
 */
export function processCourseImages(data) {
  // 如果输入为 null 或 undefined，返回空对象
  if (!data) return {};

  // 深拷贝对象
  let processed;
  try {
    processed = JSON.parse(JSON.stringify(data));
  } catch (e) {
    console.error('处理数据时出错:', e);
    return data;
  }

  /**
   * 递归处理对象中的所有属性
   * @param {Object} obj - 要处理的对象
   */
  function processObject(obj) {
    if (!obj || typeof obj !== 'object') return;

    // 处理数组
    if (Array.isArray(obj)) {
      obj.forEach(item => {
        if (typeof item === 'object' && item !== null) {
          processObject(item);
        }
      });
      return;
    }

    // 处理对象
    for (const key in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;

      const value = obj[key];

      // 跳过 Promise 对象
      if (value instanceof Promise) continue;

      // 处理字符串值
      if (typeof value === 'string') {
        // 检查是否为视频文件（优先判断，视频使用 getMediaUrl）
        if (isVideoFile(value)) {
          obj[key] = getMediaUrl(value);
        }
        // 处理图片路径
        else if (isMediaField(key)) {
          obj[key] = fixAssetUrl(value);
        }
        // 如果路径以 /images/ 或 /img/ 或 /website/ 开头，也进行处理
        else if (value.startsWith('/images/') || value.startsWith('/img/') || value.startsWith('/website/')) {
          obj[key] = fixAssetUrl(value);
        }
      }
      // 递归处理对象
      else if (typeof value === 'object' && value !== null) {
        processObject(value);
      }
    }
  }

  // 执行递归处理
  try {
    processObject(processed);
    return processed;
  } catch (e) {
    console.error('处理数据路径时出错:', e);
    return data;
  }
} 
